import { AiCompanionApi, type AiCompanionFeedbackDto } from "@simbelapp/order-sdk";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { CopilotFeedbackEnum } from "~/utils/enums/copilot/chat.enums";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { createCopilotWriteLedger, withEntry } from "~/utils/helpers/copilot/feedback-writes";
import type { ICopilotFeedbackState } from "~/utils/interfaces/copilot-interfaces";

/** Kept out of the state because nothing renders it. */
const writeLedger = createCopilotWriteLedger();

/**
 * Thumbs up / down on assistant answers, posted to `POST /ai-companion/feedback` through the SDK.
 *
 * The local state is the optimistic update: a thumb registers instantly, the POST follows.
 */
export const useCopilotFeedbackStore = defineStore("copilotFeedback", {
  state: (): ICopilotFeedbackState => {
    return { votes: {}, comments: {}, openCommentMessageId: null };
  },
  getters: {
    voteOf(state) {
      return (messageId: string): CopilotFeedbackEnum | null => state.votes[messageId] ?? null;
    },
    isCommentOpen(state) {
      return (messageId: string): boolean => state.openCommentMessageId === messageId;
    },
  },
  actions: {
    /** Clicking the active thumb again clears the vote — locally only, the API has no retraction. */
    async vote(chatId: string, messageId: string, vote: CopilotFeedbackEnum, responseId?: string) {
      if (this.votes[messageId] === vote) {
        // Counts as a write: a vote POST still in flight must not restore what was just retracted.
        writeLedger.record(messageId);

        this.votes = withEntry(this.votes, messageId, undefined);
        this.comments = withEntry(this.comments, messageId, undefined);
        if (this.openCommentMessageId === messageId) this.openCommentMessageId = null;

        return;
      }

      // Snapshot before mutating: the optimistic update has to be undoable, or a thumb stays lit on
      // feedback the backend never received — and `submit` sends the comment map, so a failed
      // comment left behind would ride along with the next vote.
      const previousVote: CopilotFeedbackEnum | undefined = this.votes[messageId];
      const previousComment: string | undefined = this.comments[messageId];
      const previousOpenCommentMessageId = this.openCommentMessageId;
      const write = writeLedger.record(messageId);

      this.votes[messageId] = vote;
      // A thumb down alone says little: offer the reason box right away.
      this.openCommentMessageId = vote === CopilotFeedbackEnum.DOWN ? messageId : null;

      if (await this.submit(chatId, messageId, responseId)) return;
      if (!writeLedger.isLatest(messageId, write)) return;

      this.votes = withEntry(this.votes, messageId, previousVote);
      this.comments = withEntry(this.comments, messageId, previousComment);
      // Only close what this write opened — another message may own the box by now.
      if (this.openCommentMessageId === messageId) this.openCommentMessageId = previousOpenCommentMessageId;
      useFeedback().error("Une erreur est survenue", "small");
    },
    async comment(chatId: string, messageId: string, comment: string, responseId?: string) {
      const previousComment: string | undefined = this.comments[messageId];
      const previousOpenCommentMessageId = this.openCommentMessageId;
      const write = writeLedger.record(messageId);

      this.comments[messageId] = comment;
      if (this.openCommentMessageId === messageId) this.openCommentMessageId = null;

      if (await this.submit(chatId, messageId, responseId)) {
        useFeedback().success("Merci, c'est noté", "small");

        return;
      }
      if (!writeLedger.isLatest(messageId, write)) return;

      // The vote stands — only the comment failed. Reopening the box is what allows another try,
      // unless another message has claimed it meanwhile.
      this.comments = withEntry(this.comments, messageId, previousComment);
      if (this.openCommentMessageId === null) this.openCommentMessageId = previousOpenCommentMessageId;
      useFeedback().error("Une erreur est survenue", "small");
    },
    closeComment() {
      this.openCommentMessageId = null;
    },
    reset() {
      writeLedger.clear();
      this.$reset();
    },
    async submit(chatId: string, messageId: string, responseId?: string): Promise<boolean> {
      const vote = this.votes[messageId];
      if (!vote) return false;

      // Conditional spreads: the API whitelists the body keys and 400s on any extra one.
      const aiCompanionFeedbackDto: AiCompanionFeedbackDto = {
        chatId,
        messageId,
        vote,
        ...(responseId && { responseId }),
        ...(this.comments[messageId] && { comment: this.comments[messageId] }),
      };

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AiCompanionApi);
        await apiInstance.submitAiCompanionFeedback({ aiCompanionFeedbackDto });

        return true;
      } catch {
        return false;
      }
    },
  },
});
